/**
 * Copyright 2026 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import * as React from "react";
import T from "i18n-react/dist/i18n-react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { visuallyHidden } from "@mui/utils";

import styles from "./styles.module.less";

import showConfirmDialog from "../showConfirmDialog";
import TableCellContent from "../table/table-cell-content";
import CustomTablePagination from "../table/CustomTablePagination";

const MuiTableSortable = ({
  columns = [],
  data = [],
  totalRows,
  perPage,
  currentPage,
  onPageChange,
  onPerPageChange,
  onSort,
  options = { sortCol: "", sortDir: 1 },
  getName = (item) => item.name,
  onEdit,
  onDelete,
  deleteDialogTitle = null,
  deleteDialogBody = null,
  onReorder,
  idKey = "id",
  updateOrderKey = "order",
  tableSx = {}
}) => {
  const { sortCol, sortDir } = options;

  const handleDragEnd = (result) => {
    if (!result.destination || result.source.index === result.destination.index)
      return;

    const reordered = [...data];
    const [movedItem] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, movedItem);

    // change value based on updateOrderKey
    if (updateOrderKey) {
      reordered.forEach((item, idx) => {
        item[updateOrderKey] = idx + 1;
      });
    }

    const movedItemId = movedItem.id;
    const newOrder = reordered.find(
      (item) => item[idKey || "id"] === movedItemId
    )?.[updateOrderKey];

    onReorder?.(reordered, movedItemId, newOrder);
  };

  const handleDelete = async (item) => {
    const isConfirmed = await showConfirmDialog({
      title: deleteDialogTitle || T.translate("general.are_you_sure"),
      text:
        typeof deleteDialogBody === "function"
          ? deleteDialogBody(getName(item))
          : deleteDialogBody ||
            `${T.translate("general.row_remove_warning")} ${getName(item)}`,
      type: "warning",
      showCancelButton: true,
      confirmButtonColor: "#DD6B55",
      confirmButtonText: T.translate("general.yes_delete")
    });

    if (isConfirmed) {
      onDelete(item.id);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper elevation={0} sx={{ width: "100%", mb: 2 }}>
        <TableContainer
          component={Paper}
          sx={{ borderRadius: 0, boxShadow: "none" }}
        >
          <Table sx={tableSx}>
            {/* TABLE HEADER */}
            <TableHead sx={{ backgroundColor: "#EAEAEA" }}>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.columnKey}
                    sx={{
                      width: col.width,
                      minWidth: col.width,
                      maxWidth: col.width
                    }}
                    align={col.align ?? "left"}
                  >
                    {col.sortable ? (
                      <TableSortLabel
                        active={sortCol === col.columnKey}
                        direction={
                          sortCol === col.columnKey && sortDir === -1
                            ? "desc"
                            : "asc"
                        }
                        onClick={() => onSort(col.columnKey, sortDir * -1)}
                      >
                        {col.header}
                        {sortCol === col.columnKey ? (
                          <Box component="span" sx={visuallyHidden}>
                            {sortDir === -1
                              ? T.translate("mui_table.sorted_desc")
                              : T.translate("mui_table.sorted_asc")}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      col.header
                    )}
                  </TableCell>
                ))}
                {onEdit && <TableCell sx={{ width: 40 }} />}
                {onDelete && <TableCell sx={{ width: 40 }} />}
                {onReorder && <TableCell sx={{ width: 40 }} />}
              </TableRow>
            </TableHead>

            {/* TABLE BODY */}
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="mui-table-droppable">
                {(droppableProvided) => (
                  <TableBody
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                  >
                    {data.map((row, rowIndex) => (
                      <Draggable
                        key={row[idKey] || rowIndex}
                        draggableId={String(row[idKey] || rowIndex)}
                        index={rowIndex}
                      >
                        {(provided, snapshot) => (
                          <TableRow
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{
                              ...(snapshot.isDragging
                                ? {
                                    display: "table",
                                    width: "100%",
                                    tableLayout: "fixed",
                                    backgroundColor: "#f0f0f0",
                                    transform: "scale(1.01)",
                                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                                    zIndex: 1,
                                    position: "relative",
                                    transition:
                                      "transform 0.2s ease, background-color 0.2s ease"
                                  }
                                : {
                                    transition: "background-color 0.2s ease"
                                  })
                            }}
                          >
                            {/* Main content columns */}
                            {columns.map((col) => (
                              <TableCell
                                key={col.columnKey}
                                align={col.align ?? "left"}
                                className={`${
                                  col.dottedBorder && styles.dottedBorderLeft
                                } ${col.className}`}
                              >
                                <TableCellContent row={row} col={col} />
                              </TableCell>
                            ))}
                            {/* Edit column */}
                            {onEdit && (
                              <TableCell
                                align="center"
                                sx={{ width: 40 }}
                                className={styles.dottedBorderLeft}
                              >
                                <IconButton
                                  size="large"
                                  onClick={() => onEdit(row)}
                                  sx={{ padding: 0 }}
                                >
                                  <EditIcon fontSize="large" />
                                </IconButton>
                              </TableCell>
                            )}
                            {/* Delete column */}
                            {onDelete && (
                              <TableCell
                                align="center"
                                sx={{ width: 40 }}
                                className={styles.dottedBorderLeft}
                              >
                                <IconButton
                                  size="large"
                                  onClick={() => handleDelete(row)}
                                  sx={{ padding: 0 }}
                                >
                                  <DeleteIcon fontSize="large" />
                                </IconButton>
                              </TableCell>
                            )}
                            {/* Re order column */}
                            {onReorder && (
                              <TableCell
                                align="center"
                                sx={{ width: 40 }}
                                className={styles.dottedBorderLeft}
                                {...provided.dragHandleProps}
                              >
                                <IconButton size="large">
                                  <UnfoldMoreIcon fontSize="large" />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                    {data.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={columns.length} align="center">
                          {T.translate("mui_table.no_items")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                )}
              </Droppable>
            </DragDropContext>
          </Table>
        </TableContainer>

        {/* PAGINATION */}
        {perPage && currentPage && onPageChange && (
          <CustomTablePagination
            totalRows={totalRows}
            perPage={perPage}
            currentPage={currentPage}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
          />
        )}
      </Paper>
    </Box>
  );
};

export default MuiTableSortable;
